import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'

interface Task {
  id: string
  name: string
  description: string
  priority: 'low' | 'medium' | 'high'
  completed: boolean
}

interface GeneratedSchedule {
  mainTasks: Array<{
    name: string
    priority: 'low' | 'medium' | 'high'
  }>
  subtasks: Array<{
    description: string
    time: number
    mainTaskName: string
  }>
}

const DemoAppPage: React.FC = () => {
  const { user } = useAuth()
  const [tasks, setTasks] = useState<Task[]>([])
  const [newTask, setNewTask] = useState({ name: '', description: '', priority: 'medium' as const })
  const [generatedSchedule, setGeneratedSchedule] = useState<GeneratedSchedule | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)

  const addTask = () => {
    if (!newTask.name.trim()) return

    const task: Task = {
      id: Date.now().toString(),
      name: newTask.name,
      description: newTask.description,
      priority: newTask.priority,
      completed: false
    }

    setTasks([...tasks, task])
    setNewTask({ name: '', description: '', priority: 'medium' })
    toast.success('Task aggiunto!')
  }

  const toggleTask = (id: string) => {
    setTasks(tasks.map(task => 
      task.id === id ? { ...task, completed: !task.completed } : task
    ))
  }

  const deleteTask = (id: string) => {
    setTasks(tasks.filter(task => task.id !== id))
    toast.success('Task eliminato!')
  }

  const generateSchedule = async () => {
    if (tasks.length === 0) {
      toast.error('Aggiungi almeno un task prima di generare lo schedule')
      return
    }

    setIsGenerating(true)
    
    // Simulate API call
    setTimeout(() => {
      const schedule: GeneratedSchedule = {
        mainTasks: tasks.map(task => ({
          name: task.name,
          priority: task.priority
        })),
        subtasks: tasks.map(task => ({
          description: `Completa ${task.name}`,
          time: 30,
          mainTaskName: task.name
        }))
      }
      
      setGeneratedSchedule(schedule)
      setIsGenerating(false)
      toast.success('Schedule generato con successo!')
    }, 2000)
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Demo App - Generatore di Schedule AI
          </h3>
          
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-4">
              Crediti disponibili: {user?.credits || 0}
            </p>
          </div>

          {/* Add Task Form */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h4 className="text-md font-medium text-gray-900 mb-3">Aggiungi Task</h4>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Nome task"
                value={newTask.name}
                onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
              <textarea
                placeholder="Descrizione (opzionale)"
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                rows={2}
              />
              <select
                value={newTask.priority}
                onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="low">Bassa Priorità</option>
                <option value="medium">Media Priorità</option>
                <option value="high">Alta Priorità</option>
              </select>
              <button
                onClick={addTask}
                className="w-full bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Aggiungi Task
              </button>
            </div>
          </div>

          {/* Tasks List */}
          <div className="mb-6">
            <h4 className="text-md font-medium text-gray-900 mb-3">I tuoi Task</h4>
            {tasks.length === 0 ? (
              <p className="text-gray-500">Nessun task aggiunto</p>
            ) : (
              <div className="space-y-2">
                {tasks.map(task => (
                  <div key={task.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={task.completed}
                        onChange={() => toggleTask(task.id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <div>
                        <p className={`font-medium ${task.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                          {task.name}
                        </p>
                        {task.description && (
                          <p className="text-sm text-gray-600">{task.description}</p>
                        )}
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          task.priority === 'high' ? 'bg-red-100 text-red-800' :
                          task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {task.priority === 'high' ? 'Alta' : task.priority === 'medium' ? 'Media' : 'Bassa'}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => deleteTask(task.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Elimina
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Generate Schedule Button */}
          <div className="mb-6">
            <button
              onClick={generateSchedule}
              disabled={isGenerating || tasks.length === 0}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isGenerating ? 'Generazione in corso...' : 'Genera Schedule AI'}
            </button>
          </div>

          {/* Generated Schedule */}
          {generatedSchedule && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="text-md font-medium text-gray-900 mb-3">Schedule Generato</h4>
              <div className="space-y-4">
                {generatedSchedule.mainTasks.map((task, index) => (
                  <div key={index} className="bg-white p-3 rounded-lg">
                    <h5 className="font-medium text-gray-900">{task.name}</h5>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      task.priority === 'high' ? 'bg-red-100 text-red-800' :
                      task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {task.priority === 'high' ? 'Alta' : task.priority === 'medium' ? 'Media' : 'Bassa'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default DemoAppPage 